import {useState} from 'react';
import {Box} from '@mantine/core';
import ArticleArt, {ArtScene} from './ArticleArt';

/**
 * Article header visual.
 *
 * Renders the photograph at `src` when one is present and loads; falls back to
 * the drawn scene otherwise. The fallback is what makes the photographs
 * optional — a path can be wired up before the file exists, and the article
 * still looks finished until someone drops the image in.
 *
 * Everything renders in a 16:9 box, matching the aspect the photographs are
 * generated at. A fixed pixel height would cover-crop them to roughly 2:1 on
 * cards and 4:1 on the article page, cutting the top and bottom off frames
 * whose subject sits low — a counter, a notebook, a pair of hands.
 */
const ArticleImage = ({
    src,
    scene,
    tone,
    seed = 0,
    alt = ''
}: {
    src?: string;
    scene?: ArtScene;
    tone?: 'brand' | 'accent' | 'mixed';
    seed?: number;
    alt?: string;
}) => {
    const [failed, setFailed] = useState(false);
    const showArt = !src || failed;

    return (
        <Box
            style={{
                width: '100%',
                aspectRatio: '16 / 9',
                borderRadius: 'var(--mantine-radius-md)',
                overflow: 'hidden'
            }}
        >
            {showArt ? (
                <ArticleArt scene={scene} tone={tone} seed={seed}/>
            ) : (
                <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    onError={() => setFailed(true)}
                    style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}}
                />
            )}
        </Box>
    );
};

export default ArticleImage;
